/**
 * 結構化資料 (JSON-LD) 管理器
 * 用於生成和注入 SEO Schema
 */
import type { RouteLocationNormalized } from 'vue-router'

// --- 基礎介面 ---

interface Organization {
  '@type': 'Organization'
  name: string
  url: string
  logo: string
  sameAs: string[]
}

interface WebSite {
  '@type': 'WebSite'
  url: string
  name:string
  potentialAction: {
    '@type': 'SearchAction'
    target: {
      '@type': 'EntryPoint'
      urlTemplate: string
    }
    'query-input': 'required name=search_term_string'
  }
}

interface WebPage {
  '@type': 'WebPage'
  url: string
  name: string
  description: string
  isPartOf: {
    '@id': string
  }
}

interface Tool {
  '@type': 'HowTo' | 'SoftwareApplication' // 根據工具類型選擇
  name: string
  description: string
  url?: string
  // 'HowTo' 類型屬性
  step?: any[] 
  // 'SoftwareApplication' 類型屬性
  applicationCategory?: string
  operatingSystem?: string
  offers?: {
    '@type': 'Offer'
    price: string
    priceCurrency: string
  }
}

class StructuredDataManager {
  private injectedSchemas: Set<string> = new Set()

  /**
   * 清除所有已注入的 Schema 腳本
   * @param type - 可選，指定要清除的 Schema 類型
   */
  public clearInjectedSchemas(type?: string): void {
    const selector = type 
      ? `script[type="application/ld+json"][data-schema-type="${type}"]`
      : 'script[type="application/ld+json"]'
      
    document.querySelectorAll(selector).forEach(el => el.remove())
    
    if (type) {
      this.injectedSchemas.delete(type)
    } else {
      this.injectedSchemas.clear()
    }
  }

  /**
   * 注入 JSON-LD Schema 到 <head>
   * @param schema - 要注入的 Schema 物件
   * @param type - Schema 的類型，用於防止重複注入
   */
  private injectSchema(schema: object, type: string): void {
    if (this.injectedSchemas.has(type)) {
      this.clearInjectedSchemas(type)
    }

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-schema-type', type)
    script.textContent = JSON.stringify(schema, null, 2)
    document.head.appendChild(script)
    this.injectedSchemas.add(type)
  }
  
  // --- Schema 生成器 ---

  public generateOrganizationSchema(): Organization {
    return {
      '@type': 'Organization',
      name: 'CallMeAPI',
      url: 'https://callmeapi.com',
      logo: 'https://callmeapi.com/logo.svg',
      sameAs: [
        'https://www.facebook.com/callmeapi',
        'https://twitter.com/callmeapi',
        'https://www.linkedin.com/company/callmeapi'
      ]
    }
  }

  public generateWebSiteSchema(): WebSite {
    return {
      '@type': 'WebSite',
      url: 'https://callmeapi.com',
      name: 'CallMeAPI',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: 'https://callmeapi.com/search?q={search_term_string}'
        },
        'query-input': 'required name=search_term_string'
      }
    }
  }

  public generateWebPageSchema(route: RouteLocationNormalized, title: string, description: string): WebPage {
    return {
      '@type': 'WebPage',
      url: `https://callmeapi.com${route.fullPath}`,
      name: title,
      description: description,
      isPartOf: {
        '@id': 'https://callmeapi.com/#website'
      }
    }
  }

  public generateToolSchema(toolInfo: { name: string; description: string; url: string; category: string }): Tool {
    return {
      '@type': 'SoftwareApplication',
      name: toolInfo.name,
      description: toolInfo.description,
      url: toolInfo.url,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      }
    }
  }

  // --- 公共方法 ---

  /**
   * 設置全域 Schema (Organization, WebSite)
   */
  public setGlobalSchemas(): void {
    const orgSchema = this.generateOrganizationSchema()
    const websiteSchema = { ...this.generateWebSiteSchema(), mainEntityOfPage: { '@id': 'https://callmeapi.com/' } }
    
    // 將 Organization schema 嵌入 WebSite schema
    const graph = {
      '@context': 'https://schema.org',
      '@graph': [orgSchema, websiteSchema]
    }

    this.injectSchema(graph, 'global')
  }

  /**
   * 為特定頁面設置 Schema
   * @param route - Vue Router 的路由物件
   * @param title - 頁面標題
   * @param description - 頁面描述
   */
  public setWebPageSchema(route: RouteLocationNormalized, title: string, description: string): void {
    const webPageSchema = this.generateWebPageSchema(route, title, description)
    this.injectSchema(webPageSchema, 'WebPage')
  }
  
  /**
   * 為工具頁面設置 Schema
   * @param toolInfo - 工具資訊
   */
  public setToolSchema(toolInfo: { name: string; description: string; url: string; category: string }): void {
    const toolSchema = this.generateToolSchema(toolInfo)
    this.injectSchema(toolSchema, 'Tool')
  }
}

export const structuredDataManager = new StructuredDataManager() 